/**
 * 线索列表 / 详情的 URL search params 契约(冷加载还原的唯一真源)。
 *
 * 纪律(feat-008 验收红线):筛选 / 分页 / 排序 / tab 全部进 URL,组件里不留筛选态。
 * 校验用 zod schema 直接交给 TanStack Router 的 `validateSearch`(standard-schema 直传),
 * 每一项都带 `.catch()` 兜底 —— 手改 URL 写出垃圾值时降级到默认档而不是把整页打成错误页。
 */

import type { LeadsListParams } from '@gta/api-client'
import { z } from 'zod'

import {
  DEFAULT_LEAD_ORDERING,
  DEFAULT_PAGE_SIZE,
  LEAD_DETAIL_TABS,
  LEAD_ORDERING_VALUES,
  LEAD_POOL_STATE_VALUES,
  LEAD_STAGE_VALUES,
  NONE_OPTION_VALUE,
  PAGE_SIZE_OPTIONS,
  PRODUCT_FAMILY_VALUES,
} from './constants'

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/** 空串一律折叠成 undefined:URL 里不留 `?search=` 这种空筛选残留。 */
const optionalText = z
  .string()
  .trim()
  .transform((value) => (value.length > 0 ? value : undefined))
  .optional()
  .catch(undefined)

const optionalIsoDate = z
  .string()
  .trim()
  .regex(ISO_DATE)
  .optional()
  .catch(undefined)

export const leadListSearchSchema = z.object({
  /** 阶段:new/contacted/proposal_sent/negotiating/won/lost */
  stage: z.enum(LEAD_STAGE_VALUES).optional().catch(undefined),
  /** 产品族:day_trip/custom_tour,或 `none`=未定(★需求 7) */
  product_family: z
    .enum([...PRODUCT_FAMILY_VALUES, NONE_OPTION_VALUE])
    .optional()
    .catch(undefined),
  /** 负责人 Employee resource_id;`none`=未分配(= 总池子入口,零新参数) */
  owner: optionalText,
  /**
   * 池态二值筛(★W11,LD-22 additive):recalled=被收回 / never=从未派发。
   * 后端恒隐含「无归属」,故它是**池内**的二分,不是与 owner 并列的第三条轴。
   */
  pool: z.enum(LEAD_POOL_STATE_VALUES).optional().catch(undefined),
  /** 获客渠道 code */
  channel: optionalText,
  /** 投放活动号(包含匹配),或 `none`=未采到(★需求 4) */
  campaign: optionalText,
  /** 目的地城市 id */
  city: z.coerce.number().int().positive().optional().catch(undefined),
  /** 关键词(客户名 / 线索资源号 / 旅行主题);与其余四域同名,直传后端 `search` */
  search: optionalText,
  /** 建档日期下界(含),YYYY-MM-DD */
  created_after: optionalIsoDate,
  /** 建档日期上界(含),YYYY-MM-DD */
  created_before: optionalIsoDate,
  page: z.coerce.number().int().min(1).default(1).catch(1),
  page_size: z.coerce
    .number()
    .int()
    .refine((value) => (PAGE_SIZE_OPTIONS as readonly number[]).includes(value))
    .default(DEFAULT_PAGE_SIZE)
    .catch(DEFAULT_PAGE_SIZE),
  ordering: z.enum(LEAD_ORDERING_VALUES).default(DEFAULT_LEAD_ORDERING).catch(DEFAULT_LEAD_ORDERING),
})

export type LeadListSearch = z.infer<typeof leadListSearchSchema>

export const leadDetailSearchSchema = z.object({
  /** 详情页 tab:跟进记录 / 阶段时间线 */
  tab: z.enum(LEAD_DETAIL_TABS).default('activities').catch('activities'),
})

export type LeadDetailSearch = z.infer<typeof leadDetailSearchSchema>

/**
 * 等于默认值的参数不回写 URL(stripSearchParams;A4 全站统一)。
 * 只列 schema 里带 `.default()` 的项 —— 其余项默认就是 undefined,本来也不会进 URL。
 */
export const LEAD_LIST_STRIP_DEFAULTS = {
  page: 1,
  page_size: DEFAULT_PAGE_SIZE,
  ordering: DEFAULT_LEAD_ORDERING,
} satisfies Partial<LeadListSearch>

export const LEAD_DETAIL_STRIP_DEFAULTS = {
  tab: 'activities',
} satisfies Partial<LeadDetailSearch>

/** 列表页筛选默认档(「重置」按钮把 URL 打回这一档)。 */
export const LEAD_LIST_SEARCH_DEFAULTS: LeadListSearch = {
  stage: undefined,
  product_family: undefined,
  owner: undefined,
  pool: undefined,
  channel: undefined,
  campaign: undefined,
  city: undefined,
  search: undefined,
  created_after: undefined,
  created_before: undefined,
  page: 1,
  page_size: DEFAULT_PAGE_SIZE,
  ordering: DEFAULT_LEAD_ORDERING,
}

/** URL search params → 后端 query params(参数名与后端逐一同名,直传)。 */
export function toLeadsListParams(search: LeadListSearch): LeadsListParams {
  return {
    stage: search.stage,
    // `none` 哨兵在两侧同名同义,故这里仍是直传(没有第二套翻译表)。
    product_family: search.product_family,
    owner: search.owner,
    // 同名直传:`pool` 在两侧一字相同,没有第二套翻译表。
    pool: search.pool,
    channel: search.channel,
    campaign: search.campaign,
    city: search.city,
    search: search.search,
    created_after: search.created_after,
    created_before: search.created_before,
    page: search.page,
    page_size: search.page_size,
    ordering: search.ordering,
  }
}

/** 是否处于「有筛选」状态(决定重置按钮是否可用、空态文案怎么写)。 */
export function hasActiveLeadFilters(search: LeadListSearch): boolean {
  return Boolean(
    search.stage ??
      search.product_family ??
      search.owner ??
      search.pool ??
      search.channel ??
      search.campaign ??
      search.city ??
      search.search ??
      search.created_after ??
      search.created_before,
  )
}
